/**
 * The Library page's load and actions (firm-model spec §8, §10.2): the firm library, your
 * own saved components and the firm's usage, plus rename and remove for `edit_library`.
 * The page stays wiring + JSX.
 */
import type { FirmLibraryList } from "@contract/library/library";
import * as React from "react";
import { ApiError } from "../api/client";
import type * as libraryApi from "../api/library";

type Api = typeof libraryApi;

/** A load failure as copy. A 404 means the panel shipped before the API (deploy skew). */
function loadErrorCopy(e: unknown): string {
  if (e instanceof ApiError && e.status === 404) return "The library isn't available yet. Try again in a few minutes.";
  return e instanceof ApiError ? e.message : "We couldn't load your library. Try again.";
}

const actionErrorCopy = (e: unknown): string => (e instanceof ApiError ? e.message : "That didn't work. Try again.");

export function useFirmLibrary(api: Api) {
  const [list, setList] = React.useState<FirmLibraryList | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [nonce, setNonce] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    api
      .fetchFirmLibrary()
      .then((next) => {
        if (!alive) return;
        setList(next);
        setLoadError(null);
      })
      .catch((e: unknown) => alive && setLoadError(loadErrorCopy(e)));
    return () => {
      alive = false;
    };
  }, [api, nonce]);

  /** Runs one action; true when it landed (the list then reloads). */
  const run = async (action: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    setActionError(null);
    try {
      await action();
      setNonce((n) => n + 1);
      return true;
    } catch (e) {
      setActionError(actionErrorCopy(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return {
    list,
    loadError,
    actionError,
    clearActionError: () => setActionError(null),
    busy,
    rename: (id: string, name: string) => run(() => api.renameFirmLibraryItem(id, name)),
    remove: (id: string) => run(() => api.removeFirmLibraryItem(id)),
  };
}
